import { lazy } from 'react'
import { Route, Routes } from 'react-router-dom'
import { StaffOnly } from '@/auth'
import { AppShell } from '@/layouts/AppShell'
import { SectionLayout } from '@/layouts/SectionLayout'
import { sectionById } from '@/layouts/nav'
import NotFoundPage from '@/layouts/NotFoundPage'
import type { Mode } from '@/theme/createInternalTheme'

// ── THE ONLY FILE ALLOWED TO NAME A SCREEN ──────────────────────────────────
// Every page is lazy. Combined with <StaffOnly> wrapping the whole tree, a
// visitor who is not staff never downloads a chunk that names a single screen —
// the bundle stops being a description of what this tool does.
//
// That property survives only because no `import()` runs outside a route
// rendered inside <StaffOnly>. `main.tsx` statically pulls in App → AppShell →
// nav.ts, so anything those modules reference is in the entry chunk: a
// `component:` field in the nav table, or a hover-prefetch handler on a tab,
// would quietly undo it. `lazyBoundary.test.ts` asserts nothing outside this
// file and `src/features/**` mentions `@/features`.
const Support = lazy(() => import('@/features/support/SupportPage'))
const SupportTicket = lazy(() => import('@/features/support/SupportTicketPage'))
const Overview = lazy(() => import('@/features/overview/OverviewPage'))
const Investors = lazy(() => import('@/features/investors/InvestorsPage'))
const Orgs = lazy(() => import('@/features/orgs/OrgsPage'))
const OrgDetail = lazy(() => import('@/features/orgDetail/OrgDetailPage'))
const People = lazy(() => import('@/features/people/PeoplePage'))
const Access = lazy(() => import('@/features/access/AccessPage'))
const Contacts = lazy(() => import('@/features/contacts/ContactsPage'))
const Funnel = lazy(() => import('@/features/funnel/FunnelPage'))
const ActivityScreen = lazy(() => import('@/features/activity/ActivityPage'))
const Communications = lazy(() => import('@/features/communications/CommunicationsPage'))
const CommunicationDetail = lazy(() => import('@/features/communications/CommunicationDetailPage'))
const CommsAudience = lazy(() => import('@/features/communications/AudiencePage'))
const CommsWebinars = lazy(() => import('@/features/communications/WebinarsPage'))
const CommsEditor = lazy(() => import('@/features/communications/CommunicationEditorPage'))
const Money = lazy(() => import('@/features/money/MoneyPage'))
const Margin = lazy(() => import('@/features/margin/MarginPage'))
const Redemptions = lazy(() => import('@/features/redemptions/RedemptionsPage'))
const Promos = lazy(() => import('@/features/promos/PromosPage'))
const PromoDetail = lazy(() => import('@/features/promos/PromoDetailPage'))
const Tokens = lazy(() => import('@/features/tokens/TokensPage'))
const Costs = lazy(() => import('@/features/costs/CostsPage'))
const Compute = lazy(() => import('@/features/compute/ComputePage'))
const StorageScreen = lazy(() => import('@/features/storage/StoragePage'))
const Projections = lazy(() => import('@/features/projections/ProjectionsPage'))
const Calculator = lazy(() => import('@/features/calculator/CalculatorPage'))
const Sources = lazy(() => import('@/features/sources/SourcesPage'))
const Calibration = lazy(() => import('@/features/calibration/CalibrationPage'))
const Watch = lazy(() => import('@/features/watch/WatchPage'))
const Admin = lazy(() => import('@/features/admin/AdminPage'))
const DataQuality = lazy(() => import('@/features/dataquality/DataQualityPage'))

/**
 * ── ZERO REDIRECTS ──────────────────────────────────────────────────────────
 * All twenty paths that existed before this restructure are still canonical;
 * `/watch` is the only addition. The sections are PATHLESS layout routes, so
 * they group the screens without contributing a URL segment — which is what
 * lets internal tabs exist without renaming a single link somebody already
 * pasted into Slack.
 *
 * That mattered more than it looks: `<Navigate to="/x"/>` drops the query
 * string, and in this app the query string carries the window, the class filter
 * and now every table's sort and filters. A redirect would have silently
 * answered a different question than the shared link asked.
 */
export default function App({ mode, onToggleMode }: { mode: Mode; onToggleMode: () => void }) {
  return (
    <StaffOnly>
      <Routes>
        <Route element={<AppShell mode={mode} onToggleMode={onToggleMode} />}>
          <Route element={<SectionLayout section={sectionById('resumen')} />}>
            <Route index element={<Overview />} />
            <Route path="investors" element={<Investors />} />
          </Route>

          <Route element={<SectionLayout section={sectionById('clientes')} />}>
            {/* DENTRO de la sección, y primero, igual que en `nav.ts`.
                Estaban fuera de todo `SectionLayout`, sueltas junto al `*`: la
                pantalla se veía y funcionaba, pero sin barra de subpestañas — así
                que desde Soporte no había forma de llegar a Organizaciones, a
                Solicitudes ni a las otras siete. Se leía como si esas pantallas
                hubieran desaparecido. `App.test.tsx` afirma ahora que ninguna
                ruta cuyo camino esté en `nav.ts` cuelgue fuera de su sección. */}
            <Route path="support" element={<Support />} />
            <Route path="support/:ticketId" element={<SupportTicket />} />
            <Route path="orgs" element={<Orgs />} />
            {/* `:slug`, and it keeps «Organizaciones» selected: `activeTab`
                matches by longest prefix, so a detail route stays inside the
                section it belongs to without being a tab of its own. */}
            <Route path="orgs/:slug" element={<OrgDetail />} />
            <Route path="people" element={<People />} />
            <Route path="access" element={<Access />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="funnel" element={<Funnel />} />
            <Route path="activity" element={<ActivityScreen />} />
            <Route path="communications" element={<Communications />} />
            <Route path="communications/audience" element={<CommsAudience />} />
            {/* The static ones are declared before `:id`, though React Router
                ranks them above a dynamic segment anyway. `webinars/:id` keeps
                «Webinars» selected by the same longest-prefix rule `orgs/:slug`
                relies on. */}
            <Route path="communications/webinars" element={<CommsWebinars />} />
            <Route path="communications/webinars/:id" element={<CommsWebinars />} />
            <Route path="communications/new" element={<CommsEditor />} />
            <Route path="communications/:id/edit" element={<CommsEditor />} />
            <Route path="communications/:id" element={<CommunicationDetail />} />
          </Route>

          <Route element={<SectionLayout section={sectionById('dinero')} />}>
            <Route path="money" element={<Money />} />
            <Route path="money/margin" element={<Margin />} />
            <Route path="redemptions" element={<Redemptions />} />
            {/* Junto a Canjes y por la misma razón: un descuento es una pregunta
                sobre el efectivo. `promos/:id` conserva «Códigos» resaltado por
                el prefijo más largo, igual que `orgs/:slug`. */}
            <Route path="promos" element={<Promos />} />
            <Route path="promos/:id" element={<PromoDetail />} />
            <Route path="tokens" element={<Tokens />} />
          </Route>

          <Route element={<SectionLayout section={sectionById('costos')} />}>
            <Route path="costs" element={<Costs />} />
            <Route path="costs/compute" element={<Compute />} />
            <Route path="storage" element={<StorageScreen />} />
            <Route path="projections" element={<Projections />} />
            <Route path="calculator" element={<Calculator />} />
          </Route>

          <Route element={<SectionLayout section={sectionById('plataforma')} />}>
            <Route path="sources" element={<Sources />} />
            <Route path="calibration" element={<Calibration />} />
            <Route path="watch" element={<Watch />} />
            {/* Llegaron en el seam de escritura (PR #17): la cola de
                revisión y la única pantalla que cambia algo. Van aquí
                porque «Plataforma» es la pregunta que contestan —
                ¿la maquinaria está sana, y qué asumimos hoy? */}
            <Route path="data-quality" element={<DataQuality />} />
            <Route path="admin" element={<Admin />} />
          </Route>

          {/* Eager, and a real page: rendering Overview here meant a mistyped
              link silently answered a different question with the wrong path
              still in the address bar. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </StaffOnly>
  )
}
